import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import ApolloLinkTimeout from 'apollo-link-timeout';
import merge from 'deepmerge';
import isEqual from 'lodash/isEqual';
import { useMemo } from 'react';

import errorHandler from './errorHandler';

// mock server url defined in the mock-server app (apps/mock-server/package.json) => scripts.start
const MOCK_SERVER_URL = 'http://0.0.0.0:4000/graphql';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';

const gqlEndpoint =
  typeof window === 'undefined' && // if running server-side
  !process.env.CI // and not running in Gitlab pipeline
    ? process.env.APOLLO_INTERNAL_CLIENT_URL || // then use Intranet hostnames
      process.env.NEXT_PUBLIC_APOLLO_INTERNAL_CLIENT_URL
    : process.env.APOLLO_CLIENT_URL ||
      process.env.NEXT_PUBLIC_APOLLO_CLIENT_URL ||
      MOCK_SERVER_URL;

const gqlCredentials =
  process.env.APOLLO_CLIENT_URL || process.env.NEXT_PUBLIC_APOLLO_CLIENT_URL
    ? {
        user:
          process.env.APOLLO_CLIENT_AUTH_USER ||
          process.env.NEXT_PUBLIC_APOLLO_CLIENT_AUTH_USER,
        password:
          process.env.APOLLO_CLIENT_AUTH_PASSWORD ||
          process.env.NEXT_PUBLIC_APOLLO_CLIENT_AUTH_PASSWORD,
      }
    : null;

let apolloClient: ApolloClient<any>;
let apolloClientDealerIds: string | undefined;

/**
 * Configure Apollo Client
 * @see https://github.com/vercel/next.js/tree/canary/examples/with-apollo
 *
 * Merging fields
 * @see https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
 */
function createApolloClient(
  dealerIds: string[] | undefined,
  merchantOrigin: string | undefined
) {
  const authorization =
    gqlCredentials && gqlCredentials.user && gqlCredentials.password
      ? `${gqlCredentials.user}:${gqlCredentials.password}`
      : null;

  // Create Apollo Links
  const httpLink = new HttpLink({
    uri: gqlEndpoint,
    credentials: authorization ? 'include' : undefined,
  });

  const authHeaders: any = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: authorization
      ? `Basic ${Buffer.from(authorization).toString('base64')}`
      : undefined,
  };

  const authLink = new ApolloLink((operation, forward) => {
    const prevContext = operation.getContext();

    operation.setContext({
      ...prevContext,
      headers: {
        ...authHeaders,
        ...prevContext.headers,
      },
    });

    // Call the next link in the middleware chain.
    return forward(operation);
  });

  const whiteLabelLink = dealerIds?.length
    ? new ApolloLink((operation, forward) => {
        const prevContext = operation.getContext();

        operation.setContext({
          ...prevContext,
          headers: {
            'white-label-merchant-uuid': dealerIds.join(','),
            'white-label-merchant-origin': merchantOrigin,
            ...prevContext.headers,
          },
        });

        return forward(operation);
      })
    : null;

  const timeoutLink = new ApolloLinkTimeout(30000);

  const retryLink = new RetryLink({
    delay: {
      initial: 1000,
      jitter: false,
    },
    attempts: {
      max: 2,
    },
  });

  const errorLink = onError(errorHandler);

  // Create new Apollo Client
  return new ApolloClient({
    link: from([
      ...(authorization ? [authLink] : []),
      ...(whiteLabelLink ? [whiteLabelLink] : []),
      retryLink,
      timeoutLink,
      errorLink,
      httpLink,
    ]),
    credentials: authorization ? 'include' : undefined,
    cache: new InMemoryCache({
      typePolicies: {},
    }),
    ssrMode: typeof window === 'undefined',
  });
}

export function initializeApollo(
  initialState = null,
  merchantUuids?: string[],
  merchantOrigin?: string
) {
  const merchantUuidsStr = merchantUuids?.length
    ? merchantUuids.join(',')
    : undefined;
  let _apolloClient;

  if (apolloClient && apolloClientDealerIds === merchantUuidsStr) {
    // reuse existing client
    _apolloClient = apolloClient;
  } else {
    // create a new client
    _apolloClient = createApolloClient(merchantUuids, merchantOrigin);
  }

  // If your page has Next.js data fetching methods that use
  // Apollo Client, the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getServerSideProps
    const data = merge(initialState, existingCache, {
      // Combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }

  // In the browser: keep a reference to the client for later reuse
  if (typeof window !== 'undefined') {
    apolloClient = _apolloClient;
    apolloClientDealerIds = merchantUuidsStr;
  }

  return _apolloClient;
}

/**
 * To be used in getServerSideProps during SSR like so
 *
 * @example
 * export const getServerSideProps = async (context) => {
 *   return addApolloState(apolloClient, {
 *     props: {}
 *   });
 * }
 */
// TODO Correctly apply addApolloState in SSR to return the state to the client
export function addApolloState(
  client: ApolloClient<any>,
  pageProps: { props: any }
) {
  if (pageProps?.props) {
    // eslint-disable-next-line
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(
  pageProps: any,
  dealerIds?: string[],
  merchantOrigin?: string
) {
  // eslint-disable-next-line
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  return useMemo(
    () => initializeApollo(state, dealerIds, merchantOrigin),
    [state, dealerIds, merchantOrigin]
  );
}

/**
 * Will reset the Apollo store on client side. Active queries will get re-fetched asynchronously.
 *
 * @see https://www.apollographql.com/docs/react/caching/advanced-topics/#resetting-the-store
 */
export function resetApolloClientStore() {
  if (typeof window !== 'undefined' && apolloClient) {
    apolloClient.resetStore();
  }
}
